import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Headphones,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Loader2,
  Download,
  Mic,
  Sparkles,
  GripVertical,
  Clock,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { getApiBase, getBackendBaseUrl } from '../../utils/apiBase';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { formatStoryDuration } from '../../utils/storyDisplay';
import { toast } from 'sonner';

const API = getApiBase();
const BACKEND_URL = getBackendBaseUrl();

function formatTotalDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function SourceBadge({ source }) {
  if (source === 'original_voice') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(88_18%_82%)] bg-[hsl(88_24%_96%)] px-2 py-0.5 text-[10px] font-medium text-[hsl(88_18%_28%)]">
        <Mic className="h-3 w-3" aria-hidden />
        Your voice
      </span>
    );
  }
  if (source === 'ai_narration') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(38_22%_84%)] bg-[hsl(42_28%_99%)] px-2 py-0.5 text-[10px] font-medium text-[hsl(32_10%_38%)]">
        <Sparkles className="h-3 w-3 text-[#8b7355]" aria-hidden />
        AI narration
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full border border-dashed border-[hsl(38_14%_82%)] px-2 py-0.5 text-[10px] text-muted-foreground">
      No audio recorded
    </span>
  );
}

export function AudiobookPlayer({ treeId, canEdit = false, className }) {
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [narratingId, setNarratingId] = useState(null);
  const [orderMode, setOrderMode] = useState('timeline');
  const [manualOrder, setManualOrder] = useState([]);
  const audioRef = useRef(null);

  const fetchPlaylist = useCallback(async () => {
    if (!treeId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trees/${treeId}/audiobook`);
      setPlaylist(res.data);
      setOrderMode(res.data.order_mode || 'timeline');
      setManualOrder((res.data.chapters || []).map((c) => c.story_id));
    } catch {
      toast.error('Could not load audiobook chapters');
    } finally {
      setLoading(false);
    }
  }, [treeId]);

  useEffect(() => {
    fetchPlaylist();
  }, [fetchPlaylist]);

  const chapters = useMemo(() => playlist?.chapters || [], [playlist?.chapters]);
  const current = chapters[activeIndex];

  const playableChapters = useMemo(
    () => chapters.filter((c) => c.audio_url && c.audio_source !== 'none'),
    [chapters]
  );

  useEffect(() => {
    if (!current?.audio_url) {
      setProgress(0);
      setDuration(0);
      return undefined;
    }
    const url = resolveAssetUrl(BACKEND_URL, current.audio_url);
    const audio = new Audio(url);
    audioRef.current = audio;
    const onTime = () => setProgress(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || current.duration_seconds || 0);
    const onEnd = () => {
      setIsPlaying(false);
      if (activeIndex < chapters.length - 1) {
        setActiveIndex((i) => i + 1);
        setIsPlaying(true);
      }
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
    };
  }, [current?.audio_url, current?.story_id, current?.duration_seconds, activeIndex, chapters.length]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !current?.audio_url) return;
    if (isPlaying) audio.play().catch(() => setIsPlaying(false));
    else audio.pause();
  }, [isPlaying, current?.audio_url, current?.story_id]);

  const hasPendingNarration = useMemo(
    () =>
      chapters.some((c) => c.narration_status === 'queued' || c.narration_status === 'processing') ||
      narratingId != null,
    [chapters, narratingId]
  );

  useEffect(() => {
    if (!hasPendingNarration) return undefined;
    const id = window.setInterval(fetchPlaylist, 5000);
    return () => window.clearInterval(id);
  }, [hasPendingNarration, fetchPlaylist]);

  const togglePlay = () => {
    if (!current?.audio_url) return;
    setIsPlaying((p) => !p);
  };

  const goPrev = () => {
    if (activeIndex > 0) {
      setActiveIndex((i) => i - 1);
      setIsPlaying(true);
    }
  };

  const goNext = () => {
    if (activeIndex < chapters.length - 1) {
      setActiveIndex((i) => i + 1);
      setIsPlaying(true);
    }
  };

  const persistOrderMode = async (mode) => {
    setOrderMode(mode);
    try {
      await axios.put(`${API}/trees/${treeId}/audiobook/settings`, {
        order_mode: mode,
        chapter_order: mode === 'manual' ? manualOrder : undefined,
      });
      await fetchPlaylist();
    } catch {
      toast.error('Could not save order preference');
    }
  };

  const moveChapter = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= manualOrder.length) return;
    const next = [...manualOrder];
    const [item] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, item);
    setManualOrder(next);
    try {
      await axios.put(`${API}/trees/${treeId}/audiobook/settings`, {
        order_mode: 'manual',
        chapter_order: next,
      });
      setOrderMode('manual');
      await fetchPlaylist();
    } catch {
      toast.error('Could not reorder chapters');
    }
  };

  const requestNarration = async (storyId) => {
    setNarratingId(storyId);
    try {
      const res = await axios.post(`${API}/stories/${storyId}/narrate`);
      toast.success(res.data?.credit_label || 'Narration queued — refresh in a moment');
      setTimeout(fetchPlaylist, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not queue narration');
    } finally {
      setNarratingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/trees/${treeId}/audiobook/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(playlist?.tree_name || 'audiobook').replace(/\s+/g, '_')}_audiobook.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Audiobook export downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className={cn('flex justify-center py-16', className)} data-testid="audiobook-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#8b7355]" aria-label="Loading audiobook" />
      </div>
    );
  }

  if (!chapters.length) {
    return (
      <div
        className={cn('rounded-2xl border border-dashed border-[hsl(38_16%_86%)] p-10 text-center', className)}
        data-testid="audiobook-empty"
      >
        <Headphones className="mx-auto h-10 w-10 text-[#8b7355]/60" aria-hidden />
        <p className="mt-4 font-serif text-lg text-[hsl(24_14%_18%)]">Your life audiobook is waiting</p>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
          Save a few memories with voice or text — they will appear here as chapters you can listen through.
        </p>
      </div>
    );
  }

  const coverUrl = current?.cover_image_url
    ? resolveAssetUrl(BACKEND_URL, current.cover_image_url)
    : null;
  const progressPct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div className={cn('space-y-6', className)} data-testid="audiobook-player">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-[hsl(32_10%_42%)]">
            Life audiobook
          </p>
          <h2 className="mt-1 font-serif text-2xl font-normal text-[hsl(24_14%_16%)]">{playlist?.tree_name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {chapters.length} {chapters.length === 1 ? 'chapter' : 'chapters'}
            {playlist?.totals?.total_duration_seconds
              ? ` · ${formatTotalDuration(playlist.totals.total_duration_seconds)}`
              : ''}
          </p>
          {playlist?.narration_minutes_used > 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">
              AI narration used: ~{playlist.narration_minutes_used.toFixed(1)} minute credits this archive
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant={orderMode === 'timeline' ? 'secondary' : 'outline'}
            className="rounded-full"
            onClick={() => persistOrderMode('timeline')}
          >
            Timeline order
          </Button>
          <Button
            type="button"
            size="sm"
            variant={orderMode === 'manual' ? 'secondary' : 'outline'}
            className="rounded-full"
            onClick={() => persistOrderMode('manual')}
          >
            Manual order
          </Button>
          {playlist?.export_ready ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              disabled={exporting}
              onClick={handleExport}
              data-testid="audiobook-export-btn"
            >
              {exporting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
              Export ZIP
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="rounded-2xl border border-[hsl(38_16%_86%)] bg-[hsl(42_28%_99%)] p-5 sm:p-6">
          <div className="aspect-[16/10] overflow-hidden rounded-xl border border-[hsl(38_14%_88%)] bg-gradient-to-br from-[hsl(42_28%_96%)] to-[hsl(38_16%_90%)]">
            {coverUrl ? (
              <img
                src={coverUrl}
                alt={current?.cover_image_alt || current?.title || 'Chapter cover'}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-[hsl(32_10%_42%)]">
                <Headphones className="h-10 w-10 opacity-60" aria-hidden />
                <p className="text-xs uppercase tracking-widest">Listening</p>
              </div>
            )}
          </div>

          <div className="mt-4 space-y-2">
            <SourceBadge source={current?.audio_source} />
            <h3 className="font-serif text-xl text-[hsl(24_14%_16%)]">{current?.title}</h3>
            {current?.person_name ? (
              <p className="text-sm text-muted-foreground">{current.person_name}</p>
            ) : null}
            {current?.summary ? (
              <p className="line-clamp-3 text-sm leading-relaxed text-[hsl(32_10%_38%)]">{current.summary}</p>
            ) : null}
          </div>

          <div className="mt-5">
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-[hsl(38_16%_90%)]"
              role="progressbar"
              aria-valuenow={Math.round(progressPct)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-[hsl(43_48%_58%)] transition-[width] duration-200"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="mt-1 flex justify-between text-[11px] tabular-nums text-muted-foreground">
              <span>{formatStoryDuration(progress) || '0:00'}</span>
              <span>{formatStoryDuration(duration || current?.duration_seconds) || '—'}</span>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-3">
            <Button type="button" size="icon" variant="outline" className="rounded-full" onClick={goPrev} disabled={activeIndex <= 0}>
              <SkipBack className="h-5 w-5" aria-hidden />
              <span className="sr-only">Previous chapter</span>
            </Button>
            <Button
              type="button"
              size="icon"
              className="h-12 w-12 rounded-full"
              onClick={togglePlay}
              disabled={!current?.audio_url}
              data-testid="audiobook-play-btn"
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" fill="currentColor" />}
              <span className="sr-only">{isPlaying ? 'Pause' : 'Play'}</span>
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="rounded-full"
              onClick={goNext}
              disabled={activeIndex >= chapters.length - 1}
            >
              <SkipForward className="h-5 w-5" aria-hidden />
              <span className="sr-only">Next chapter</span>
            </Button>
          </div>

          {canEdit && current?.audio_source === 'none' && current?.has_transcript ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4 w-full rounded-full"
              disabled={narratingId === current.story_id}
              onClick={() => requestNarration(current.story_id)}
            >
              {narratingId === current.story_id ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Create AI narration for this chapter
            </Button>
          ) : null}
        </div>

        <div className="rounded-2xl border border-[hsl(38_16%_86%)] bg-white/60 p-3 sm:p-4">
          <p className="mb-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Chapters
          </p>
          <ul className="max-h-[min(32rem,70vh)] space-y-1 overflow-y-auto" role="list">
            {chapters.map((ch, idx) => (
              <li key={ch.story_id}>
                <div
                  className={cn(
                    'flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left transition-colors',
                    idx === activeIndex
                      ? 'bg-[hsl(42_28%_98%)] ring-1 ring-[hsl(43_48%_58%_/_0.35)]'
                      : 'hover:bg-[hsl(42_26%_99%)]'
                  )}
                >
                  {orderMode === 'manual' && canEdit ? (
                    <div className="flex flex-col gap-0.5 pt-1">
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground text-[10px]"
                        onClick={() => moveChapter(idx, idx - 1)}
                        aria-label="Move chapter up"
                      >
                        ▲
                      </button>
                      <GripVertical className="h-3 w-3 text-muted-foreground/50" aria-hidden />
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground text-[10px]"
                        onClick={() => moveChapter(idx, idx + 1)}
                        aria-label="Move chapter down"
                      >
                        ▼
                      </button>
                    </div>
                  ) : (
                    <span className="w-6 pt-1 text-center text-xs tabular-nums text-muted-foreground">{idx + 1}</span>
                  )}
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => {
                      setActiveIndex(idx);
                      setIsPlaying(Boolean(ch.audio_url));
                    }}
                  >
                    <p className="font-serif text-sm text-[hsl(24_14%_18%)] line-clamp-2">{ch.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <SourceBadge source={ch.audio_source} />
                      {ch.duration_seconds ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground tabular-nums">
                          <Clock className="h-3 w-3" aria-hidden />
                          {formatStoryDuration(ch.duration_seconds)}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-3 px-2 text-[11px] leading-relaxed text-muted-foreground">
            {playableChapters.length} of {chapters.length} chapters have audio. Original voice is always preferred over
            AI narration.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AudiobookPlayer;
